import { useTranslation } from 'react-i18next'
import { visibleQuestionIds, type SurveyAnswer, type SurveyDetail, type SurveyQuestion } from 'shared'
import { formatDate } from '../../lib/dates'
import TableField from './TableField'

/** Every question with the answer it got, and who wrote it. */
export default function AnswerList({
  survey,
  answers,
  locale,
}: {
  survey: SurveyDetail
  answers: SurveyAnswer[]
  locale: string
}) {
  const { t } = useTranslation()
  const byQuestion = new Map(answers.map((answer) => [answer.question_id, answer]))
  // What was asked, judged on these answers: a question a condition left out was
  // not left unanswered, and the list says so instead of showing a dash.
  const asked = visibleQuestionIds(
    survey.questions,
    Object.fromEntries(answers.map((answer) => [answer.question_id, answer.value])),
  )

  return (
    <>
      {survey.questions.map((question) => {
        if (question.type === 'section') {
          return asked.has(question.id) ? (
            <div key={question.id} className="bg-gray-50 px-4 py-2 dark:bg-gray-900/40">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                {question.label}
              </p>
            </div>
          ) : null
        }

        if (!asked.has(question.id)) {
          return (
            <div key={question.id} className="p-4" data-skipped>
              <p className="text-sm font-medium text-gray-400 dark:text-gray-500">{question.label}</p>
              <p className="mt-1 text-sm italic text-gray-400 dark:text-gray-500">{t('survey.notAsked')}</p>
            </div>
          )
        }

        const answer = byQuestion.get(question.id)
        return (
          <div key={question.id} className="p-4">
            <p className="text-sm font-medium text-gray-900 dark:text-gray-50">{question.label}</p>
            {question.type === 'table' && Array.isArray(answer?.value) ? (
              // Rows and columns read as a table, not as a sentence.
              <div className="mt-2">
                <TableField question={question} value={answer.value} onChange={() => undefined} readOnly />
              </div>
            ) : (
              <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                {formatAnswer(question, answer?.value ?? null, locale, t)}
              </p>
            )}
            {answer?.answered_by_name && (
              <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
                {answer.answered_by_name}
              </p>
            )}
          </div>
        )
      })}
    </>
  )
}


export function formatAnswer(
  question: SurveyQuestion,
  value: unknown,
  locale: string,
  t: (key: string) => string,
): string {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'boolean') return t(value ? 'common.yes' : 'common.no')
  if (Array.isArray(value)) return value.map((entry) => optionLabel(question, entry)).join(', ')
  if (question.type === 'date' && typeof value === 'string') return formatDate(value, locale)
  return optionLabel(question, value)
}

/**
 * A choice answer stores the option's value, which need not read like the
 * option did — a survey written by an agent can label `arr` "Series A ready".
 */
function optionLabel(question: SurveyQuestion, value: unknown): string {
  const match = question.config?.options?.find((option) => String(option.value) === String(value))
  return match ? match.label : String(value)
}
